import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { Tags, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCategoriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', name: '', description: '', required: false, sort_order: 500, active: true });

  const load = async () => {
    setLoading(true);
    try { setRows(await api.adminCategories()); }
    catch { toast.error('Could not load categories'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || !form.name) return toast.error('Code and name required');
    try {
      await api.adminCreateCategory(form);
      toast.success('Category created');
      setForm({ code: '', name: '', description: '', required: false, sort_order: 500, active: true });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create'); }
  };

  const update = async (row) => {
    try {
      await api.adminUpdateCategory(row.id, {
        code: row.code, name: row.name, description: row.description,
        required: row.required, sort_order: row.sort_order, active: row.active,
      });
      toast.success('Saved');
    } catch { toast.error('Could not save'); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this category? (will be deactivated if still in use)')) return;
    try { await api.adminDeleteCategory(id); await load(); }
    catch { toast.error('Could not delete'); }
  };

  return (
    <AdminLayout title="Document Categories" subtitle="Configure category structure and required checklist.">
      <div className="bg-white rounded-xl border border-neutral-200 p-5 mb-6">
        <h3 className="text-[15px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-[#c89200]" /> Add category</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input data-testid="cat-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="code (e.g. t4)"
                 className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px]" />
          <input data-testid="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Display name"
                 className="md:col-span-2 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px]" />
          <input value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || 0, 10) })} placeholder="Sort"
                 className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px]" />
          <label className="inline-flex items-center gap-2 text-[12.5px] px-3">
            <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} /> Required
          </label>
        </div>
        <input data-testid="cat-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
               placeholder="Description"
               className="mt-3 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px]" />
        <div className="mt-3 flex justify-end">
          <button data-testid="cat-create" onClick={create} className="bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold rounded-md text-[13px] px-4 py-2 inline-flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Create
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-[#14110f] flex items-center gap-2"><Tags className="w-4 h-4 text-[#c89200]" /> All categories</h3>
        </div>
        {loading ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">Loading…</p>
          : <table className="w-full text-[13.5px]" data-testid="cats-table">
              <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-5 py-2.5">Code</th>
                  <th className="text-left px-5 py-2.5">Name</th>
                  <th className="text-left px-5 py-2.5">Description</th>
                  <th className="text-right px-5 py-2.5">Sort</th>
                  <th className="text-center px-5 py-2.5">Required</th>
                  <th className="text-center px-5 py-2.5">Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="px-5 py-2 font-mono text-[12px] text-neutral-600">{r.code}</td>
                    <td className="px-5 py-2">
                      <input value={r.name} onChange={(e) => setRows((p) => p.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                             className="w-full px-2 py-1.5 rounded border border-neutral-200 text-[13px]" />
                    </td>
                    <td className="px-5 py-2">
                      <input value={r.description || ''} onChange={(e) => setRows((p) => p.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                             className="w-full px-2 py-1.5 rounded border border-neutral-200 text-[13px]" />
                    </td>
                    <td className="px-5 py-2 text-right">
                      <input value={r.sort_order} type="number" onChange={(e) => setRows((p) => p.map((x, i) => i === idx ? { ...x, sort_order: parseInt(e.target.value || 0, 10) } : x))}
                             className="w-20 text-right px-2 py-1.5 rounded border border-neutral-200 text-[13px]" />
                    </td>
                    <td className="px-5 py-2 text-center">
                      <input type="checkbox" checked={r.required} onChange={(e) => setRows((p) => p.map((x, i) => i === idx ? { ...x, required: e.target.checked } : x))} />
                    </td>
                    <td className="px-5 py-2 text-center">
                      <input type="checkbox" checked={r.active} onChange={(e) => setRows((p) => p.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x))} />
                    </td>
                    <td className="px-5 py-2 text-right whitespace-nowrap">
                      <button onClick={() => update(r)} className="text-[12px] text-[#c89200] hover:underline inline-flex items-center gap-1 mr-3"><Save className="w-3.5 h-3.5" /> Save</button>
                      <button onClick={() => remove(r.id)} className="text-[12px] text-rose-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </AdminLayout>
  );
}
